import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Row, Col, Form, Input, Button } from "antd";
import { useAuthState } from "react-firebase-hooks/auth";
import { sendPasswordResetEmail, confirmPasswordReset } from "firebase/auth";
import { auth } from "../../firebaseConfig";
import { UserOutlined, LockOutlined, ArrowLeftOutlined, MailOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { Card, Typography, message, Divider } from "antd";

export default function PasswordReset() {
  const [form] = Form.useForm();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const { Title, Text } = Typography;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode");
  const apiKey = queryParams.get("apiKey");
  const oobCode = queryParams.get("oobCode");

  const isResetMode = mode === "resetPassword";

  useEffect(() => {
    if (authLoading) return;
    if (user) navigate("/");
  }, [user, authLoading, navigate]);

  const handlePasswordReset = async () => {
    setLoading(true);
    try {
      if (isResetMode) {
        if (apiKey === process.env.REACT_APP_API_FIREBASE_KEY) {
          await confirmPasswordReset(auth, oobCode, newPassword);
          form.resetFields();
          message.success("Password updated successfully! You can now login with your new password.");
          navigate("/login");
        } else {
          message.error("Invalid reset link or expired token");
        }
      } else {
        await sendPasswordResetEmail(auth, email);
        form.resetFields();
        message.success("If your email is in our system, you will receive password reset instructions shortly.");
      }
    } catch (error) {
      console.log(error);
      switch (error.code) {
        case "auth/invalid-action-code":
          message.error("Reset password link is invalid or has expired");
          break;
        case "auth/weak-password":
          message.error("Password is too weak");
          break;
        case "auth/user-not-found":
          message.error("No account found with this email address");
          break;
        default:
          message.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '120px 20px 80px',
      position: 'relative'
    }}>
      {/* Background decorations */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '10%',
        width: '200px',
        height: '200px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '50%',
        animation: 'float 6s ease-in-out infinite'
      }}></div>
      
      <div style={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: '150px',
        height: '150px',
        background: 'rgba(255, 215, 0, 0.2)',
        borderRadius: '50%',
        animation: 'float 4s ease-in-out infinite reverse'
      }}></div>

      {/* Back to Login Button */}
      <Link 
        to="/login" 
        style={{
          position: 'absolute',
          top: '100px',
          left: '30px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: 'white',
          textDecoration: 'none',
          padding: '10px 16px',
          borderRadius: '25px',
          background: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          transition: 'all 0.3s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
        }}
      >
        <ArrowLeftOutlined />
        <span>Back to Login</span>
      </Link>

      <div style={{ 
        width: '100%', 
        maxWidth: '1200px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Row justify="center" align="middle" style={{ width: '100%' }}>
          <Col xs={24} sm={20} md={16} lg={12} xl={10}>
            <Card
              style={{
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                border: 'none',
                background: 'white',
                margin: '0 auto'
              }}
              bodyStyle={{ padding: '50px 40px' }}
            >
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: isResetMode ? 
                    'linear-gradient(135deg, #10b981, #059669)' : 
                    'linear-gradient(135deg, #667eea, #764ba2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  fontSize: '32px',
                  color: 'white'
                }}>
                  {isResetMode ? <LockOutlined /> : <MailOutlined />}
                </div>
                
                <Title 
                  level={2} 
                  style={{ 
                    color: '#1f2937', 
                    marginBottom: '8px',
                    fontSize: '2rem',
                    fontWeight: 'bold'
                  }}
                >
                  {isResetMode ? "Set New Password" : "Forgot Password?"}
                </Title>
                
                <Text style={{ 
                  color: '#6b7280', 
                  fontSize: '16px',
                  lineHeight: '1.5'
                }}>
                  {isResetMode ? 
                    "Enter your new password below to complete the reset process." :
                    "No worries! Enter your email and we'll send you reset instructions."
                  }
                </Text>
              </div>

              {/* Form */}
              <Form
                form={form}
                layout="vertical"
                onFinish={handlePasswordReset}
                autoComplete="off"
                requiredMark={false}
              >
                {isResetMode ? (
                  <Form.Item
                    name="password"
                    label={<span style={{ fontWeight: '600', color: '#374151' }}>New Password</span>}
                    rules={[
                      {
                        required: true,
                        message: "Please enter your new password",
                      },
                      {
                        min: 8,
                        message: "Password must be at least 8 characters long",
                      },
                      {
                        pattern: new RegExp(
                          "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&;:,])[A-Za-z\\d@$!%*?&;:,]{8,}$"
                        ),
                        message:
                          "Password must contain uppercase, lowercase, number, and special character",
                      },
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                      placeholder="Enter your new password"
                      size="large"
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        padding: '12px 16px'
                      }}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="email"
                    label={<span style={{ fontWeight: '600', color: '#374151' }}>Email Address</span>}
                    rules={[
                      {
                        required: true,
                        message: "Please enter your email address",
                      },
                      {
                        type: 'email',
                        message: "Please enter a valid email address",
                      }
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                      placeholder="Enter your email address"
                      size="large"
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        padding: '12px 16px'
                      }}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Form.Item>
                )}

                {/* Password Requirements for Reset Mode */}
                {isResetMode && (
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '30px'
                  }}>
                    <Text style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                      Password Requirements:
                    </Text>
                    <ul style={{
                      margin: '8px 0 0 0',
                      paddingLeft: '20px',
                      fontSize: '12px',
                      color: '#64748b',
                      lineHeight: '1.5'
                    }}>
                      <li>At least 8 characters long</li>
                      <li>One uppercase and lowercase letter</li>
                      <li>One number and special character</li>
                    </ul>
                  </div>
                )}

                {/* Submit Button */}
                <Form.Item style={{ marginBottom: '30px' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={loading}
                    style={{
                      width: '100%',
                      height: '50px',
                      borderRadius: '12px',
                      background: isResetMode ? 
                        'linear-gradient(135deg, #10b981, #059669)' :
                        'linear-gradient(135deg, #667eea, #764ba2)',
                      border: 'none',
                      fontSize: '16px',
                      fontWeight: '600',
                      boxShadow: isResetMode ? 
                        '0 4px 15px rgba(16, 185, 129, 0.3)' :
                        '0 4px 15px rgba(102, 126, 234, 0.3)'
                    }}
                  >
                    {loading ? 
                      (isResetMode ? 'Updating Password...' : 'Sending Instructions...') :
                      (isResetMode ? 'Update Password' : 'Send Reset Instructions')
                    }
                  </Button>
                </Form.Item>

                {/* Help Text */}
                {!isResetMode && (
                  <div style={{
                    textAlign: 'center',
                    padding: '16px',
                    background: '#f0f9ff',
                    borderRadius: '8px',
                    border: '1px solid #bae6fd',
                    marginBottom: '30px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                      <CheckCircleOutlined style={{ color: '#0284c7', fontSize: '16px' }} />
                      <Text style={{ fontSize: '14px', color: '#0284c7', fontWeight: '500' }}>
                        Security Notice
                      </Text>
                    </div>
                    <Text style={{ fontSize: '12px', color: '#0369a1', lineHeight: '1.5' }}>
                      For your security, reset instructions will only be sent to registered email addresses. 
                      Check your spam folder if you don't receive the email within a few minutes.
                    </Text>
                  </div>
                )}
              </Form>

              {/* Navigation Links */}
              <div style={{ 
                textAlign: 'center', 
                paddingTop: '20px', 
                borderTop: '1px solid #e5e7eb' 
              }}>
                <Text style={{ color: '#6b7280' }}>
                  Remember your password?{' '}
                  <Link 
                    to="/login"
                    style={{
                      color: '#667eea',
                      fontWeight: '600',
                      textDecoration: 'none'
                    }}
                  >
                    Back to Login
                  </Link>
                </Text>
                {!isResetMode && (
                  <>
                    <Divider type="vertical" style={{ margin: '0 8px' }} />
                    <Link 
                      to="/register"
                      style={{
                        color: '#667eea',
                        fontWeight: '600',
                        textDecoration: 'none'
                      }}
                    >
                      Create Account
                    </Link>
                  </>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </div>
  );
}